import { BACKEND_URL } from '../../utils/apiConfig';

// Build an authenticated file URL usable directly in <img src>
export const fileUrl = (fileId) => {
  if (!fileId) return '';
  const token = localStorage.getItem('token') || '';
  return `${BACKEND_URL}/api/files/${fileId}?auth=${encodeURIComponent(token)}`;
};

// Public (no-auth) media URL for shareable landing pages.
export const publicMediaUrl = (fileId) => (fileId ? `${BACKEND_URL}/api/files/${fileId}` : '');

export const CATEGORY_FORMATS = ['singles', 'doubles', 'mixed', 'team'];
export const EVENT_STATUSES = [
  'draft', 'published', 'registration_open', 'registration_closed',
  'ongoing', 'completed', 'cancelled',
];
export const PAYMENT_MODES = ['Cash', 'UPI', 'Card', 'Cashfree', 'Pending'];
export const TSHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
export const GENDERS = ['male', 'female', 'other'];
export const FOOD_PREFS = ['Veg', 'Non-Veg', 'Jain'];
export const SELF_RATINGS = ['Beginner', 'Intermediate', 'Expert'];

export const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  published: 'bg-blue-100 text-blue-700',
  registration_open: 'bg-emerald-100 text-emerald-700',
  registration_closed: 'bg-amber-100 text-amber-700',
  ongoing: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-gray-200 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
};

export const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Today's date in IST (YYYY-MM-DD) — matches the backend's coupon window
// check (`_resolve_event_coupon_discount` uses `datetime.now(IST)`), so
// coupon valid_from/valid_to comparisons stay consistent near midnight IST.
export const todayIST = () => {
  const ist = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().slice(0, 10);
};

// Format a YYYY-MM-DD date string as "Sunday, 6 December 2025"
export const formatEventDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T12:00:00');  // noon avoids DST ambiguity
    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
    const day = d.getDate();
    const month = d.toLocaleDateString('en-US', { month: 'long' });
    const year = d.getFullYear();
    return `${weekday}, ${day} ${month} ${year}`;
  } catch { return dateStr; }
};

// Compute "Closes in X days" from a registration_end date string.
// Returns { days: number, label: string } or null if already closed / no date.
export const regCountdown = (endDateStr) => {
  if (!endDateStr) return null;
  try {
    const today = new Date(todayIST() + 'T12:00:00');
    const end = new Date(endDateStr + 'T12:00:00');
    const diff = Math.round((end - today) / (1000 * 60 * 60 * 24));
    if (diff < 0) return null;
    if (diff === 0) return { days: 0, label: 'Closes today' };
    return { days: diff, label: `Registration closes in ${diff} day${diff !== 1 ? 's' : ''}` };
  } catch { return null; }
};

// Distinct color themes cycled per category to differentiate them visually.
const CATEGORY_PALETTE = [
  { bg: 'bg-violet-50', border: 'border-violet-300', ring: 'ring-violet-400', dot: 'bg-violet-500', text: 'text-violet-700', chip: 'bg-violet-100 text-violet-700' },
  { bg: 'bg-emerald-50', border: 'border-emerald-300', ring: 'ring-emerald-400', dot: 'bg-emerald-500', text: 'text-emerald-700', chip: 'bg-emerald-100 text-emerald-700' },
  { bg: 'bg-amber-50', border: 'border-amber-300', ring: 'ring-amber-400', dot: 'bg-amber-500', text: 'text-amber-700', chip: 'bg-amber-100 text-amber-700' },
  { bg: 'bg-sky-50', border: 'border-sky-300', ring: 'ring-sky-400', dot: 'bg-sky-500', text: 'text-sky-700', chip: 'bg-sky-100 text-sky-700' },
  { bg: 'bg-rose-50', border: 'border-rose-300', ring: 'ring-rose-400', dot: 'bg-rose-500', text: 'text-rose-700', chip: 'bg-rose-100 text-rose-700' },
  { bg: 'bg-indigo-50', border: 'border-indigo-300', ring: 'ring-indigo-400', dot: 'bg-indigo-500', text: 'text-indigo-700', chip: 'bg-indigo-100 text-indigo-700' },
  { bg: 'bg-teal-50', border: 'border-teal-300', ring: 'ring-teal-400', dot: 'bg-teal-500', text: 'text-teal-700', chip: 'bg-teal-100 text-teal-700' },
  { bg: 'bg-fuchsia-50', border: 'border-fuchsia-300', ring: 'ring-fuchsia-400', dot: 'bg-fuchsia-500', text: 'text-fuchsia-700', chip: 'bg-fuchsia-100 text-fuchsia-700' },
];
export const categoryColor = (index) => CATEGORY_PALETTE[index % CATEGORY_PALETTE.length];

// Explicit customer-auth header — player pages must NOT rely on axios.defaults,
// which the admin AuthContext can overwrite when both tokens exist in one browser.
export const cAuth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('customer_token') || ''}` },
});
